import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useResizeDetector } from 'react-resize-detector';
import styled, { css } from 'styled-components';

import Row from 'components/common/Row';
import Text from 'components/common/Text';
import ProfileThumbnail from 'components/common/ProfileThumbnail';
import Column from 'components/common/Column';
import NavigateForm from 'containers/ProductList/components/NavigateForm';
import {
  ICON_MENU,
  ICON_MENU_DELIVERY,
  ICON_MENU_HEADPHONE,
  ICON_MENU_INDIVIDUAL,
  ICON_MENU_LOGOUT,
  ICON_MENU_NOTICE,
  ICON_MENU_PROFILE,
  ICON_MENU_QNA,
  ICON_MENU_REVIEW,
  ICON_MENU_WITHDRAW,
  ICON_SETTLEMENT_LIST,
  ICON_SETTLEMENT_REQUEST,
  ICON_SWITCH,
} from 'assets/icons';
import Divider from 'components/Divider';
import {
  CONCIERGE_PATH,
  INDIVIDUAL_PATH,
  NOTICE_PATH,
  PROFILE_PATH,
  QNA_LIST_PATH,
  REVIEW_LIST_PATH,
  SETTLEMENT_REQUEST_PATH,
  SETTLEMENT_PATH,
  SUPER_HOST_NOTICE,
  DELIVERY_PATH,
} from 'constants/path';
import {
  callWebviewHandler,
  isWebview,
  WebviewOperatorType,
} from 'utils/webview';
import HostApplicationContainer from './components/HostApplicationContainer';
import { useMenuHooks } from './hooks/useMenuHooks';
import CurrentBanners from './components/CurrentBanners';
import DashBoardContainer from './components/DashBoardContainer';
import { STORAGE_KEY } from 'utils/localStoarge';
import Footer from 'components/common/Footer';
import { signoutHook } from 'utils/SignoutHooks';
import { useQueryGetQnaCount } from 'containers/QnaList/graphQL/hooks/useQueryGetQnaCount';
import HackleManager from 'utils/hackle';

import packageJson from '../../../package.json';
import CommonImage from 'components/common/CommonImage';
import { MAX_WIDTH } from 'App';
import WrapperContainer from 'components/common/Wrapper';

const WebVersion: string = packageJson.version;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
  max-width: 768px;
  background-color: #fff;
`;

const SwitchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: fixed;
  bottom: 85px;

  padding: 12px;
  border-radius: 99px;
  background-color: #1b1c1e;
  z-index: 15;
  color: var(--Static-White, #fff);

  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px; /* 137.5% */
  letter-spacing: -0.6px;
`;

const BadgeItem = styled.div<{ color: string; background: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6px 8px;

  border-radius: 8px;
  background: ${props => props.background};
  color: ${props => props.color};
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 13px; /* 130% */
  letter-spacing: -0.1px;
`;

const MyInfoSheet = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: absolute;
  right: 0px;
  top: 45px;
  padding: 16px 20px;
  width: 200px;
  border-radius: 12px;
  background: var(--Static-White, #fff);
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.15);
  z-index: 20;
`;

const MenuPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { handleSignout } = signoutHook();
  const { width, ref } = useResizeDetector();

  const { qnaCount } = useQueryGetQnaCount();
  const isMobile = width ? width <= MAX_WIDTH : window.innerWidth <= MAX_WIDTH;
  const [isOpenMyInfoSheet, setIsOpenMyInfoSheet] = useState<boolean>(false);

  const {
    currentBanners,
    hostDashBoard,
    isOpenHostApplicationModal,
    setIsOpenProfileUpdateModal,
    setIsOpenHostApplicationModal,
  } = useMenuHooks();

  useEffect(() => {
    if (location.state) {
      setIsOpenProfileUpdateModal(true);
    }
  }, [location.state]);

  const renderMyInfoSheet = (): JSX.Element => {
    return (
      <MyInfoSheet>
        <NavigateForm
          icon={ICON_MENU_PROFILE}
          title="프로필 관리"
          onClick={() => {
            navigate(PROFILE_PATH);

            HackleManager.logProfileMenuClick('프로필 관리');
            setIsOpenMyInfoSheet(false);
          }}
          iconStyle={{ width: '18px', height: '18px' }}
        />
        <NavigateForm
          icon={ICON_MENU_INDIVIDUAL}
          title="연락처 설정"
          onClick={() => {
            navigate(INDIVIDUAL_PATH);

            HackleManager.logProfileMenuClick('호스트 연락처 설정');
            setIsOpenMyInfoSheet(false);
          }}
          iconStyle={{ width: '18px', height: '18px' }}
        />
        {(localStorage.getItem(STORAGE_KEY.IS_HOST_ADMIN_APP) === 'Y' ||
          !isWebview()) && (
          <React.Fragment>
            <Divider />
            <NavigateForm
              icon={ICON_MENU_LOGOUT}
              title="로그아웃"
              onClick={() => {
                handleSignout();
              }}
            />
          </React.Fragment>
        )}
        {localStorage.getItem(STORAGE_KEY.IS_HOST_ADMIN_APP) === 'Y' && (
          <NavigateForm
            icon={ICON_MENU_WITHDRAW}
            title="회원 탈퇴"
            onClick={() => {
              callWebviewHandler(WebviewOperatorType.WithDrawHandler);
            }}
          />
        )}
      </MyInfoSheet>
    );
  };

  const renderHostApplicationContainer = (): JSX.Element => {
    return (
      <HostApplicationContainer
        isMobile={isMobile}
        onClose={() => setIsOpenHostApplicationModal(false)}
      />
    );
  };

  return (
    <React.Fragment>
      <WrapperContainer isMobile={isMobile}>
        <Container ref={ref}>
          <Column style={{ padding: isMobile ? '0 0 64px' : '24px 40px 64px' }}>
            {isMobile && (
              <Row
                justifyContent="space-between"
                style={{
                  position: 'sticky',
                  top: 0,
                  padding: '24px 20px',
                  width: '100%',
                  maxHeight: '84px',
                  background: '#fff',
                  zIndex: 10,
                }}
              >
                <Text color="#1b1c1e" size={isMobile ? '24px' : '20px'}>
                  메뉴
                </Text>
                <Row
                  gap="6px"
                  onClick={() => {
                    setIsOpenMyInfoSheet(!isOpenMyInfoSheet);
                  }}
                  style={{ position: 'relative', height: '36px' }}
                >
                  <ProfileThumbnail />
                  <CommonImage src={ICON_MENU} />
                  {isOpenMyInfoSheet && renderMyInfoSheet()}
                </Row>
              </Row>
            )}
            <Column gap="8px">
              {hostDashBoard && (
                <Column style={{ padding: isMobile ? '0' : '0' }}>
                  <DashBoardContainer
                    isMobile={isMobile}
                    hostDashBoard={hostDashBoard}
                  />
                </Column>
              )}
              <Column gap="32px" style={{ padding: isMobile ? '0 20px' : '0' }}>
                {currentBanners && (
                  <CurrentBanners
                    isMobile={isMobile}
                    currentBanners={currentBanners}
                  />
                )}
                <Column gap="32px">
                  <NavigateForm
                    icon={ICON_SETTLEMENT_REQUEST}
                    title="정산 신청"
                    onClick={() => {
                      HackleManager.logMenuItemClick('정산 정보 등록');

                      navigate(SETTLEMENT_REQUEST_PATH);
                    }}
                  />
                  <NavigateForm
                    icon={ICON_SETTLEMENT_LIST}
                    title="정산 정보 관리"
                    onClick={() => {
                      HackleManager.logMenuItemClick('정산 내역 및 지급 요청');

                      navigate(SETTLEMENT_PATH);
                    }}
                  />
                  <Divider />
                  <NavigateForm
                    icon={ICON_MENU_QNA}
                    title="문의 관리"
                    onClick={() => {
                      HackleManager.logMenuItemClick('문의 관리');

                      navigate(QNA_LIST_PATH);
                    }}
                    badgeContent={
                      <BadgeItem background="#EBF4FD" color="#0071E9">
                        답변 대기 {qnaCount.new.toLocaleString()}건
                      </BadgeItem>
                    }
                  />
                  <NavigateForm
                    icon={ICON_MENU_REVIEW}
                    title="후기 관리"
                    onClick={() => {
                      HackleManager.logMenuItemClick('후기 관리');

                      navigate(REVIEW_LIST_PATH);
                    }}
                  />
                  <Divider />
                  <NavigateForm
                    icon={ICON_MENU_DELIVERY}
                    title="배송 관리"
                    onClick={() => {
                      HackleManager.logMenuItemClick('배송 관리');

                      navigate(DELIVERY_PATH);
                    }}
                  />
                  <Divider />
                  <NavigateForm
                    icon={ICON_MENU_NOTICE}
                    title="공지사항"
                    onClick={() => {
                      HackleManager.logMenuItemClick('공지사항');

                      navigate(NOTICE_PATH);
                    }}
                  />
                  <NavigateForm
                    icon={ICON_MENU_HEADPHONE}
                    title="호스트 고객센터"
                    onClick={() => {
                      HackleManager.logMenuItemClick('호스트 고객센터');

                      setIsOpenHostApplicationModal(true);
                    }}
                  />
                </Column>
                <Row
                  gap="8px"
                  justifyContent="center"
                  style={{
                    padding: '12px 16px',
                    marginTop: '16px',
                    width: '100%',
                    background: '#f7f7f8',
                    borderRadius: '12px',
                  }}
                >
                  <a
                    href={SUPER_HOST_NOTICE}
                    target="_blank"
                    style={{
                      minWidth: '144px',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    <Text color="#414B56">슈퍼호스트 안내</Text>
                  </a>
                  <Text color="#DBDCDF">|</Text>
                  <a
                    href={CONCIERGE_PATH}
                    target="_blank"
                    style={{
                      minWidth: '144px',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    <Text color="#414B56">컨시어지 서비스</Text>
                  </a>
                </Row>
                <Column gap="12px" style={{ marginTop: '16px' }}>
                  <Text color="#70737C" size="12px" weight={400}>
                    버전 {WebVersion}
                  </Text>
                </Column>
              </Column>
            </Column>
            {isWebview() &&
              !localStorage.getItem(STORAGE_KEY.IS_HOST_ADMIN_APP) && (
                <SwitchButton
                  onClick={() => {
                    callWebviewHandler(WebviewOperatorType.CloseWebView);
                    localStorage.removeItem(STORAGE_KEY.AUTHORIZATION);
                  }}
                >
                  <img src={ICON_SWITCH} alt="switch icon" />
                  크루 모드로 전환
                </SwitchButton>
              )}
            {isOpenHostApplicationModal && renderHostApplicationContainer()}
          </Column>
        </Container>
      </WrapperContainer>

      <Footer />
    </React.Fragment>
  );
};

export default MenuPage;
